import {
	HalfFloatType,
	LinearSRGBColorSpace,
	Matrix4,
	NearestFilter,
	NoBlending,
	NoColorSpace,
	NodeMaterial,
	NodeUpdateType,
	QuadMesh,
	RenderTarget,
	RendererUtils,
	TempNode,
	Vector2,
	Vector3
} from 'three/webgpu';
import {
	Fn,
	abs,
	exp,
	float,
	max,
	min,
	mix,
	normalize,
	outputStruct,
	passTexture,
	property,
	texture,
	uniform,
	uv,
	vec2,
	vec3,
	vec4
} from 'three/tsl';

const RECONSTRUCTION_TAPS = Object.freeze( [
	[ - 1, - 1, 0.3679 ], [ 0, - 1, 0.6065 ], [ 1, - 1, 0.3679 ],
	[ - 1, 0, 0.6065 ], [ 0, 0, 1 ], [ 1, 0, 0.6065 ],
	[ - 1, 1, 0.3679 ], [ 0, 1, 0.6065 ], [ 1, 1, 0.3679 ]
] );

function rayDirectionWorld( sampleUV, projectionInverse, cameraWorld ) {

	const clip = vec4(
		sampleUV.x.mul( 2 ).sub( 1 ),
		sampleUV.y.oneMinus().mul( 2 ).sub( 1 ),
		1,
		1
	);
	const view = projectionInverse.mul( clip );
	const viewDirection = normalize( view.xyz.div( max( abs( view.w ), 0.00001 ) ) );
	return normalize( cameraWorld.mul( vec4( viewDirection, 0 ) ).xyz );

}

function projectWorldToPrevious( worldPosition, previousView, previousProjection ) {

	const view = previousView.mul( vec4( worldPosition, 1 ) );
	const clip = previousProjection.mul( view );
	const inverseW = float( 1 ).div( max( abs( clip.w ), 0.00001 ) );
	const ndc = clip.xy.mul( inverseW );
	return vec3(
		ndc.x.mul( 0.5 ).add( 0.5 ),
		ndc.y.mul( 0.5 ).add( 0.5 ).oneMinus(),
		clip.w
	);

}

/**
 * Cloud-specific temporal upsampler. It accumulates premultiplied cloud
 * radiance and optical depth without requiring opaque-scene velocity buffers.
 */
export class CloudTemporalResolveNode extends TempNode {

	constructor( colorNode, dataNode, camera, options = {} ) {

		super( 'vec4' );

		this.isCloudTemporalResolveNode = true;
		this.updateBeforeType = NodeUpdateType.FRAME;
		this.colorNode = colorNode;
		this.dataNode = dataNode;
		this.camera = camera;
		this.options = {
			enabled: options.enabled !== false,
			historyWeight: options.historyWeight ?? 0.88,
			depthThreshold: options.depthThreshold ?? 24,
			alphaThreshold: options.alphaThreshold ?? 0.18,
			motionRejection: options.motionRejection ?? 72,
			depthSharpness: options.depthSharpness ?? 0.08,
			alphaSharpness: options.alphaSharpness ?? 10,
			clampExpansion: options.clampExpansion ?? 0.08,
			detailBlur: options.detailBlur ?? 1
		};

		this.uniforms = {
			enabled: uniform( this.options.enabled ? 1 : 0 ),
			historyWeight: uniform( this.options.historyWeight ),
			depthThreshold: uniform( this.options.depthThreshold ),
			alphaThreshold: uniform( this.options.alphaThreshold ),
			motionRejection: uniform( this.options.motionRejection ),
			depthSharpness: uniform( this.options.depthSharpness ),
			alphaSharpness: uniform( this.options.alphaSharpness ),
			clampExpansion: uniform( this.options.clampExpansion ),
			detailBlur: uniform( this.options.detailBlur ),
			deformationRate: uniform( 0 ),
			historyValid: uniform( 0 ),
			outputSize: uniform( new Vector2( 1, 1 ) ),
			cameraWorld: uniform( new Matrix4().copy( camera.matrixWorld ) ),
			cameraView: uniform( new Matrix4().copy( camera.matrixWorldInverse ) ),
			cameraProjection: uniform( new Matrix4().copy( camera.projectionMatrix ) ),
			cameraProjectionInverse: uniform( new Matrix4().copy( camera.projectionMatrixInverse ) ),
			cameraPosition: uniform( new Vector3().copy( camera.position ) ),
			previousCameraWorld: uniform( new Matrix4().copy( camera.matrixWorld ) ),
			previousView: uniform( new Matrix4().copy( camera.matrixWorldInverse ) ),
			previousProjection: uniform( new Matrix4().copy( camera.projectionMatrix ) ),
			previousProjectionInverse: uniform( new Matrix4().copy( camera.projectionMatrixInverse ) ),
			previousCameraPosition: uniform( new Vector3().copy( camera.position ) ),
			motionDelta: uniform( new Vector3() )
		};

		this._historyTarget = new RenderTarget( 1, 1, { depthBuffer: false, type: HalfFloatType, count: 2 } );
		this._historyTarget.textures[ 0 ].name = 'CyborgClouds temporal history color';
		this._historyTarget.textures[ 1 ].name = 'CyborgClouds temporal history data';
		this._resolveTarget = new RenderTarget( 1, 1, { depthBuffer: false, type: HalfFloatType, count: 2 } );
		this._resolveTarget.textures[ 0 ].name = 'CyborgClouds temporal resolved color';
		this._resolveTarget.textures[ 1 ].name = 'CyborgClouds temporal resolved data';
		this._historyTarget.textures[ 0 ].colorSpace = LinearSRGBColorSpace;
		this._historyTarget.textures[ 1 ].colorSpace = NoColorSpace;
		this._resolveTarget.textures[ 0 ].colorSpace = LinearSRGBColorSpace;
		this._resolveTarget.textures[ 1 ].colorSpace = NoColorSpace;
		this._historyTarget.textures[ 1 ].minFilter = NearestFilter;
		this._historyTarget.textures[ 1 ].magFilter = NearestFilter;
		this._resolveTarget.textures[ 1 ].minFilter = NearestFilter;
		this._resolveTarget.textures[ 1 ].magFilter = NearestFilter;

		this._resolveMaterial = new NodeMaterial();
		this._resolveMaterial.name = 'CyborgClouds temporal resolve';
		this._resolveMaterial.toneMapped = false;
		this._resolveMaterial.transparent = true;
		this._resolveMaterial.blending = NoBlending;
		this._seedMaterial = new NodeMaterial();
		this._seedMaterial.name = 'CyborgClouds temporal seed';
		this._seedMaterial.toneMapped = false;
		this._seedMaterial.transparent = true;
		this._seedMaterial.blending = NoBlending;
		this._quad = new QuadMesh();
		this._textureNode = passTexture( this, this._resolveTarget.textures[ 0 ] );
		this._dataTextureNode = passTexture( this, this._resolveTarget.textures[ 1 ] );
		this._rendererState = undefined;
		this._motionOffset = new Vector3();
		this._previousMotionOffset = new Vector3();

	}

	getTextureNode() {

		return this._textureNode;

	}

	getDataTextureNode() {

		return this._dataTextureNode;

	}

	setOptions( patch = {} ) {

		const ranges = {
			historyWeight: [ 0, 0.96 ],
			depthThreshold: [ 1, 200 ],
			alphaThreshold: [ 0.01, 1 ],
			motionRejection: [ 1, 512 ],
			depthSharpness: [ 0.001, 1 ],
			alphaSharpness: [ 0.1, 50 ],
			clampExpansion: [ 0, 0.5 ],
			detailBlur: [ 0, 4 ]
		};

		for ( const [ key, range ] of Object.entries( ranges ) ) {

			if ( Number.isFinite( patch[ key ] ) ) {

				const value = Math.min( range[ 1 ], Math.max( range[ 0 ], patch[ key ] ) );
				this.options[ key ] = value;
				this.uniforms[ key ].value = value;

			}

		}

		if ( patch.enabled !== undefined ) {

			this.options.enabled = Boolean( patch.enabled );
			this.uniforms.enabled.value = this.options.enabled ? 1 : 0;
			this.reset();

		}

		return this;

	}

	setMotionOffset( offset ) {

		if ( offset?.isVector3 ) this._motionOffset.copy( offset );
		return this;

	}

	setDeformationRate( rate ) {

		this.uniforms.deformationRate.value = Number.isFinite( rate ) ? Math.max( 0, rate ) : 0;
		return this;

	}

	reset() {

		this.uniforms.historyValid.value = 0;
		return this;

	}

	setSize( width, height ) {

		if ( this._historyTarget.width === width && this._historyTarget.height === height ) return;
		this._historyTarget.setSize( width, height );
		this._resolveTarget.setSize( width, height );
		this.uniforms.outputSize.value.set( width, height );

	}

	updateBefore( frame ) {

		const { renderer } = frame;
		const size = renderer.getDrawingBufferSize( new Vector2() );
		const restart = this._resolveTarget.width !== size.width || this._resolveTarget.height !== size.height;

		this.uniforms.previousCameraWorld.value.copy( this.uniforms.cameraWorld.value );
		this.uniforms.previousView.value.copy( this.uniforms.cameraView.value );
		this.uniforms.previousProjection.value.copy( this.uniforms.cameraProjection.value );
		this.uniforms.previousProjectionInverse.value.copy( this.uniforms.cameraProjectionInverse.value );
		this.uniforms.previousCameraPosition.value.copy( this.uniforms.cameraPosition.value );
		this.uniforms.cameraWorld.value.copy( this.camera.matrixWorld );
		this.uniforms.cameraView.value.copy( this.camera.matrixWorldInverse );
		this.uniforms.cameraProjection.value.copy( this.camera.projectionMatrix );
		this.uniforms.cameraProjectionInverse.value.copy( this.camera.projectionMatrixInverse );
		this.uniforms.cameraPosition.value.copy( this.camera.position );
		this.uniforms.motionDelta.value.copy( this._motionOffset ).sub( this._previousMotionOffset );

		this._rendererState = RendererUtils.resetRendererState( renderer, this._rendererState ?? undefined );
		this.setSize( size.width, size.height );

		if ( restart ) {

			renderer.initRenderTarget( this._historyTarget );
			renderer.initRenderTarget( this._resolveTarget );
			renderer.setRenderTarget( this._historyTarget );
			this._quad.material = this._seedMaterial;
			this._quad.name = 'CyborgClouds temporal seed';
			this._quad.render( renderer );
			this.uniforms.historyValid.value = 0;

		}

		renderer.setRenderTarget( this._resolveTarget );
		this._quad.material = this._resolveMaterial;
		this._quad.name = 'CyborgClouds temporal resolve';
		this._quad.render( renderer );

		renderer.copyTextureToTexture( this._resolveTarget.textures[ 0 ], this._historyTarget.textures[ 0 ] );
		renderer.copyTextureToTexture( this._resolveTarget.textures[ 1 ], this._historyTarget.textures[ 1 ] );
		this.uniforms.historyValid.value = 1;
		this._previousMotionOffset.copy( this._motionOffset );

		RendererUtils.restoreRendererState( renderer, this._rendererState );

	}

	setup() {

		const historyColorNode = texture( this._historyTarget.textures[ 0 ] );
		const historyDataNode = texture( this._historyTarget.textures[ 1 ] );
		const colorOutput = property( 'vec4', 'cyborgTemporalColor' );
		const dataOutput = property( 'vec4', 'cyborgTemporalData' );
		const outputNode = outputStruct( colorOutput, dataOutput );

		const resolve = Fn( () => {

			const outputUV = uv();
			const inputTexel = vec2( 1 ).div( vec2( this.colorNode.size() ) );
			const centerColor = this.colorNode.sample( outputUV );
			const centerData = this.dataNode.sample( outputUV );
			const colorAccumulator = vec4( 0 ).toVar();
			const dataAccumulator = vec4( 0 ).toVar();
			const weightAccumulator = float( 0 ).toVar();
			const dataWeightAccumulator = float( 0 ).toVar();
			const neighborhoodMin = vec4( 65504 ).toVar();
			const neighborhoodMax = vec4( - 65504 ).toVar();

			for ( const [ x, y, spatialWeight ] of RECONSTRUCTION_TAPS ) {

				const sampleUV = outputUV.add( inputTexel.mul( vec2( x, y ) ).mul( this.uniforms.detailBlur ) );
				const sampledColor = this.colorNode.sample( sampleUV );
				const dataSample = this.dataNode.sample( sampleUV );
				const colorSample = vec4( sampledColor.rgb, dataSample.b );
				const alphaGuide = exp( abs( dataSample.b.sub( centerData.b ) ).mul( this.uniforms.alphaSharpness.negate() ) );
				const depthGuide = exp( abs( dataSample.r.sub( centerData.r ) ).mul( this.uniforms.depthSharpness.negate() ) );
				const cloudPresence = max( dataSample.b, centerData.b ).clamp();
				const weight = float( spatialWeight ).mul( alphaGuide ).mul( mix( 1, depthGuide, cloudPresence ) );
				const dataWeight = weight.mul( max( dataSample.b, 0.0001 ) );

				colorAccumulator.addAssign( colorSample.mul( weight ) );
				dataAccumulator.addAssign( dataSample.mul( dataWeight ) );
				weightAccumulator.addAssign( weight );
				dataWeightAccumulator.addAssign( dataWeight );
				neighborhoodMin.assign( min( neighborhoodMin, colorSample ) );
				neighborhoodMax.assign( max( neighborhoodMax, colorSample ) );

			}

			const currentColor = colorAccumulator.div( max( weightAccumulator, 0.0001 ) ).toVar();
			const currentData = dataAccumulator.div( max( dataWeightAccumulator, 0.0001 ) ).toVar();
			currentData.b.assign( currentColor.a );

			const currentRay = rayDirectionWorld( outputUV, this.uniforms.cameraProjectionInverse, this.uniforms.cameraWorld );
			const currentWorldPosition = this.uniforms.cameraPosition.add( currentRay.mul( currentData.g ) );
			const previousFeaturePosition = currentWorldPosition.sub( this.uniforms.motionDelta );
			const projectedHistory = projectWorldToPrevious(
				previousFeaturePosition,
				this.uniforms.previousView,
				this.uniforms.previousProjection
			);
			const historyUV = projectedHistory.xy;
			const sampledHistoryColor = historyColorNode.sample( historyUV );
			const historyData = historyDataNode.sample( historyUV );
			const historyColor = vec4( sampledHistoryColor.rgb, historyData.b );
			const previousRay = rayDirectionWorld(
				historyUV,
				this.uniforms.previousProjectionInverse,
				this.uniforms.previousCameraWorld
			);
			const previousFrontWorld = this.uniforms.previousCameraPosition
				.add( previousRay.mul( historyData.r ) )
				.add( this.uniforms.motionDelta );
			const previousMedianWorld = this.uniforms.previousCameraPosition
				.add( previousRay.mul( historyData.g ) )
				.add( this.uniforms.motionDelta );
			const historyFrontCurrent = previousFrontWorld.sub( this.uniforms.cameraPosition ).length();
			const historyMedianCurrent = previousMedianWorld.sub( this.uniforms.cameraPosition ).length();
			const historyDataCurrent = vec4( historyFrontCurrent, historyMedianCurrent, historyData.b, 1 );

			const validUV = historyUV.greaterThanEqual( 0 ).all()
				.and( historyUV.lessThanEqual( 1 ).all() )
				.and( projectedHistory.z.greaterThan( 0 ) );
			const allowedDepthDifference = max( this.uniforms.depthThreshold, currentData.r.mul( 0.04 ) );
			const validDepth = abs( historyFrontCurrent.sub( currentData.r ) ).lessThan( allowedDepthDifference );
			const validAlpha = abs( historyData.b.sub( currentData.b ) ).lessThan( this.uniforms.alphaThreshold );
			const motionPixels = outputUV.sub( historyUV ).mul( this.uniforms.outputSize ).length();
			const validMotion = motionPixels.lessThan( max( this.uniforms.motionRejection, 1 ).mul( 1.5 ) );
			const validHistory = validUV.and( validDepth ).and( validAlpha ).and( validMotion )
				.and( this.uniforms.historyValid.greaterThan( 0.5 ) )
				.and( this.uniforms.enabled.greaterThan( 0.5 ) );
			const motionFactor = motionPixels.div( max( this.uniforms.motionRejection, 1 ) ).clamp();
			const deformationCurrentWeight = this.uniforms.deformationRate.mul( 0.75 ).clamp( 0, 0.65 );
			const baseCurrentWeight = max( this.uniforms.historyWeight.oneMinus(), deformationCurrentWeight );
			const temporalCurrentWeight = max( baseCurrentWeight, motionFactor );
			const currentWeight = validHistory.select( temporalCurrentWeight, float( 1 ) );

			const neighborhoodRange = neighborhoodMax.sub( neighborhoodMin );
			const expansion = neighborhoodRange.mul( this.uniforms.clampExpansion ).add( 0.0005 );
			const clippedHistory = historyColor.clamp(
				neighborhoodMin.sub( expansion ),
				neighborhoodMax.add( expansion )
			);
			const resolvedColor = mix( clippedHistory, currentColor, currentWeight );
			const resolvedData = mix( historyDataCurrent, currentData, currentWeight );

			colorOutput.assign( resolvedColor );
			dataOutput.assign( resolvedData );

			return vec4( 0 );

		} );

		this._resolveMaterial.colorNode = resolve();
		this._resolveMaterial.outputNode = outputNode;
		this._seedMaterial.colorNode = Fn( () => {

			const seedUV = uv();
			const sampledSeedColor = this.colorNode.sample( seedUV );
			const seedData = this.dataNode.sample( seedUV ).toVar();
			const seedColor = vec4( sampledSeedColor.rgb, seedData.b );
			colorOutput.assign( seedColor );
			dataOutput.assign( seedData );
			return vec4( 0 );

		} )();
		this._seedMaterial.outputNode = outputNode;

		return this._textureNode;

	}

	dispose() {

		this._historyTarget.dispose();
		this._resolveTarget.dispose();
		this._resolveMaterial.dispose();
		this._seedMaterial.dispose();

	}

}
